import React from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { GlobalStyles, alpha, useTheme } from "@mui/material";
import { TranslateContext } from "../../context/TranslateContext";
import { UserContext } from "../../context/UserContext";
import {
  buildTourSteps,
  getTourMeta,
  resolveTourIdForPage,
  TOUR_META,
  TOUR_TO_PAGE,
} from "./tourConfigs";
import {
  clearTourCompleted,
  isTourCompleted,
  markTourCompleted,
} from "./tourStorage";

const ProductTourContext = React.createContext(null);

const AUTO_START_DELAY_MS = 900;
const TARGET_WAIT_MS = 3500;
const TARGET_POLL_MS = 120;

function waitForTargets(steps, timeoutMs = TARGET_WAIT_MS) {
  return new Promise((resolve) => {
    const started = Date.now();
    const check = () => {
      const ready = steps.every((step) => {
        if (!step?.target || step.placement === "center") return true;
        try {
          return Boolean(document.querySelector(step.target));
        } catch {
          return false;
        }
      });
      if (ready || Date.now() - started >= timeoutMs) {
        resolve(ready);
        return;
      }
      window.setTimeout(check, TARGET_POLL_MS);
    };
    check();
  });
}

function filterAvailableSteps(steps) {
  return steps.filter((step) => {
    if (!step?.target || step.placement === "center") return true;
    try {
      return Boolean(document.querySelector(step.target));
    } catch {
      return false;
    }
  });
}

export function ProductTourProvider({ children, selectedMenu, onNavigate }) {
  const theme = useTheme();
  const { lang } = React.useContext(TranslateContext);
  const { userData } = React.useContext(UserContext) || {};
  const uid = userData?.uid;

  const [run, setRun] = React.useState(false);
  const [tourId, setTourId] = React.useState(null);
  const [stepIndex, setStepIndex] = React.useState(0);
  const [steps, setSteps] = React.useState([]);
  const autoStartedRef = React.useRef(new Set());
  const selectedMenuRef = React.useRef(selectedMenu);
  selectedMenuRef.current = selectedMenu;

  const stopTour = React.useCallback((completed) => {
    setRun(false);
    setStepIndex(0);
    setSteps([]);
    setTourId((prev) => {
      if (completed && prev) {
        const meta = getTourMeta(prev);
        markTourCompleted(uid, prev, meta?.version || 1);
      }
      return null;
    });
  }, [uid]);

  const startTour = React.useCallback(
    async (id, { force = false } = {}) => {
      const meta = getTourMeta(id);
      if (!meta) return false;

      if (!force && isTourCompleted(uid, id, meta.version)) {
        return false;
      }

      const preferredPage = TOUR_TO_PAGE[id];
      if (
        preferredPage &&
        typeof onNavigate === "function" &&
        selectedMenuRef.current !== preferredPage
      ) {
        onNavigate(preferredPage);
        await new Promise((r) => window.setTimeout(r, 450));
      }

      // Let feature pages reset to the tab that exposes tour targets.
      window.dispatchEvent(
        new CustomEvent("beyundai:prepare-tour", { detail: { tourId: id } }),
      );
      await new Promise((r) => window.setTimeout(r, 120));

      const built = buildTourSteps(id, lang);
      if (!built.length) return false;

      await waitForTargets(built);
      const available = filterAvailableSteps(built);
      if (!available.length) return false;

      if (force) clearTourCompleted(uid, id);

      setTourId(id);
      setSteps(available);
      setStepIndex(0);
      setRun(true);
      return true;
    },
    [lang, uid, onNavigate],
  );

  const resetTour = React.useCallback(
    (id) => {
      clearTourCompleted(uid, id);
      autoStartedRef.current.delete(id);
    },
    [uid],
  );

  const restartTour = React.useCallback(
    (id) => startTour(id, { force: true }),
    [startTour],
  );

  // Auto-start once per session when landing on a mapped page.
  React.useEffect(() => {
    const id = resolveTourIdForPage(selectedMenu);
    if (!id || !uid) return undefined;
    if (run) return undefined;

    const meta = getTourMeta(id);
    if (!meta || isTourCompleted(uid, id, meta.version)) return undefined;
    if (autoStartedRef.current.has(id)) return undefined;

    const timer = window.setTimeout(async () => {
      autoStartedRef.current.add(id);
      await startTour(id, { force: false });
    }, AUTO_START_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [selectedMenu, uid, run, startTour]);

  const handleJoyride = React.useCallback(
    (data) => {
      const { action, index, status, type } = data;

      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        stopTour(status === STATUS.FINISHED || status === STATUS.SKIPPED);
        return;
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        const next = index + (action === ACTIONS.PREV ? -1 : 1);
        setStepIndex(next);
      }
    },
    [stopTour],
  );

  const joyrideStyles = React.useMemo(() => {
    const isDark = theme.palette.mode === "dark";
    const paper = theme.palette.background.paper;
    const primary = theme.palette.primary.main;
    // Joyride paints Next/Done with primaryColor; force readable ink on that fill.
    const onPrimary = theme.palette.getContrastText(primary);
    return {
      options: {
        arrowColor: paper,
        backgroundColor: paper,
        overlayColor: isDark ? alpha("#000", 0.72) : alpha("#000", 0.45),
        primaryColor: primary,
        textColor: theme.palette.text.primary,
        spotlightShadow: isDark
          ? `0 0 0 4px ${alpha(primary, 0.35)}`
          : `0 0 0 4px ${alpha(primary, 0.25)}`,
        zIndex: theme.zIndex.modal + 2,
      },
      tooltip: {
        borderRadius: 12,
        padding: 16,
        border: `1px solid ${
          isDark ? alpha("#fff", 0.1) : alpha("#000", 0.08)
        }`,
        boxShadow: isDark
          ? `0 16px 48px ${alpha("#000", 0.55)}`
          : `0 16px 40px ${alpha("#000", 0.18)}`,
      },
      tooltipTitle: {
        fontSize: 15,
        fontWeight: 800,
        marginBottom: 6,
        color: theme.palette.text.primary,
      },
      tooltipContent: {
        fontSize: 13.5,
        lineHeight: 1.5,
        padding: "4px 0 8px",
        color: theme.palette.text.secondary,
      },
      buttonNext: {
        borderRadius: 8,
        fontWeight: 700,
        fontSize: 13,
        padding: "8px 14px",
        backgroundColor: primary,
        color: onPrimary,
        outline: "none",
      },
      buttonBack: {
        borderRadius: 8,
        fontWeight: 600,
        fontSize: 13,
        color: theme.palette.text.secondary,
        marginRight: 8,
      },
      buttonSkip: {
        fontWeight: 600,
        fontSize: 13,
        color: theme.palette.text.secondary,
      },
      buttonClose: {
        color: theme.palette.text.secondary,
      },
      spotlight: {
        borderRadius: 12,
      },
    };
  }, [theme]);

  const locale = React.useMemo(
    () => ({
      back: lang?.tourBack || "Back",
      close: lang?.tourClose || "Close",
      last: lang?.tourDone || "Done",
      next: lang?.tourNext || "Next",
      skip: lang?.tourSkip || "Skip",
      open: lang?.tourOpen || "Open the dialog",
    }),
    [lang],
  );

  const value = React.useMemo(
    () => ({
      startTour,
      restartTour,
      resetTour,
      stopTour: () => stopTour(false),
      activeTourId: tourId,
      isRunning: run,
      tourCatalog: Object.values(TOUR_META),
      isCompleted: (id) => {
        const meta = getTourMeta(id);
        return isTourCompleted(uid, id, meta?.version || 1);
      },
    }),
    [startTour, restartTour, resetTour, stopTour, tourId, run, uid],
  );

  const primary = theme.palette.primary.main;
  const onPrimary = theme.palette.getContrastText(primary);

  return (
    <ProductTourContext.Provider value={value}>
      {children}
      <GlobalStyles
        styles={{
          // Beat any global `button { color/background }` rules that wash out Joyride CTA text.
          '.react-joyride__tooltip button[data-action="primary"]': {
            backgroundColor: `${primary} !important`,
            color: `${onPrimary} !important`,
            WebkitTextFillColor: `${onPrimary} !important`,
            border: "none !important",
          },
        }}
      />
      <Joyride
        continuous
        run={run}
        steps={steps}
        stepIndex={stepIndex}
        callback={handleJoyride}
        showProgress
        showSkipButton
        scrollToFirstStep
        disableOverlayClose
        disableScrolling={false}
        spotlightClicks={false}
        styles={joyrideStyles}
        locale={locale}
        floaterProps={{
          disableAnimation: false,
          styles: {
            floater: { filter: "none" },
          },
        }}
      />
    </ProductTourContext.Provider>
  );
}

export function useProductTour() {
  const ctx = React.useContext(ProductTourContext);
  if (!ctx) {
    throw new Error("useProductTour must be used within ProductTourProvider");
  }
  return ctx;
}

/** Safe hook when provider may be absent (e.g. story mounts). */
export function useProductTourOptional() {
  return React.useContext(ProductTourContext);
}
